/**
 * The two download outcomes that cannot be reached against a healthy API (HS-1484).
 *
 * The download toast draws five states. Three of them — Queued, Ready, Canceled — happen on any
 * normal run. **Failed** needs a ticket the byte route refuses, and **Unknown** needs a transfer
 * that goes silent for a minute; neither is something a reviewer, a designer or a backend engineer
 * can produce on demand against a server that is working.
 *
 * Until HS-1484 this switch lived in `src/tmp/fixtures/files.ts` and was read from permanent code,
 * which is exactly backwards: the fixtures are the throwaway half. It lives here now, and every
 * caller is behind `import.meta.env.DEV`, which Vite replaces with a literal `false` in a
 * production build so the branch is tree-shaken out — the switch does not ship.
 */

/** Query-string key that picks a download outcome. Development builds only. */
export const DEBUG_QUERY_PARAM = 'state';

/** What a debugged download does instead of transferring. `null` is the normal path. */
export type DownloadDebugOutcome = 'failed' | 'hang' | null;

/**
 * Pure so it is testable without touching `window` — the caller feeds it `location.search`.
 *
 * `import.meta.env.DEV` is true under Vitest, so the states stay reachable from a test as well as
 * from a development server.
 */
export function downloadDebugOutcome(search: string): DownloadDebugOutcome {
  if (!import.meta.env.DEV) return null;
  const state = new URLSearchParams(search).get(DEBUG_QUERY_PARAM);
  if (state === 'downloadFailed') return 'failed';
  if (state === 'downloadUnknown') return 'hang';
  return null;
}
